import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import {
  PAGAMENTOS_INSCRICAO_FILTRADOS,
  PagamentosInscricaoFiltradosData,
  PagamentosInscricaoFiltradosVars,
  PagamentoInscricao,
} from '../services/pagamentoInscricaoService';
import { TORNEIOS_FILTRADOS, TorneiosFiltradosData, TorneiosFiltradosVars } from '../services/torneioService';
import { buscarLigasPorNome } from '../services/ligaService';
import type { StatusPagamento } from '../types/pagamento';
import type { PeriodoFinanceiro } from '../types/financeiro';

// AC-13 (plano AC-A2) -- período e liga vêm por parâmetro (donos são o hub/aba, não o hook,
// mesmo padrão de PorLigaTab recebendo `periodo` por prop). Filtros locais (torneio/atleta/
// status) e paginação são estado deste hook. Uma única useQuery, `fetchPolicy: 'no-cache'`
// (useFinanceiroPlataforma.ts:61-67) -- nenhuma soma monetária aqui, a coluna "Valor" da tabela
// lê `item.valor` diretamente.

export interface FiltrosTransacoesPlataforma {
  torneioId: string | null;
  atletaId: string | null;
  status: StatusPagamento | null;
}

export interface UseTransacoesPlataformaResult {
  items: PagamentoInscricao[];
  totalItems: number;
  page: number;
  setPage: (page: number) => void;
  filtros: FiltrosTransacoesPlataforma;
  setFiltros: (filtros: FiltrosTransacoesPlataforma) => void;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
  // Reusado pela toolbar (PaymentsTableToolbar.fetchTorneios) — TransacoesTab.tsx não pode
  // importar services/ diretamente (VALIDATE da Task 6), então a busca remota debounced de
  // torneios (cascateada por `ligaId` do hub) vive aqui, no hook.
  fetchTorneios: (q: string) => Promise<Array<{ id: string; label: string }>>;
  // Mesmo motivo de fetchTorneios: desde 2026-08-18 o filtro de liga vive DENTRO da aba
  // Transações (antes era um filtro no topo do hub, que aparentava valer para as 5 abas quando
  // só valia para 2), e a aba continua proibida de importar services/.
  fetchLigas: (q: string) => Promise<Array<{ id: string; label: string }>>;
}

const FILTROS_INICIAIS: FiltrosTransacoesPlataforma = {
  torneioId: null,
  atletaId: null,
  status: null,
};

export function useTransacoesPlataforma(
  periodo: PeriodoFinanceiro,
  ligaId: string | null,
): UseTransacoesPlataformaResult {
  const [filtros, setFiltros] = useState<FiltrosTransacoesPlataforma>(FILTROS_INICIAIS);
  const [page, setPage] = useState(0);

  const query = useQuery<PagamentosInscricaoFiltradosData, PagamentosInscricaoFiltradosVars>(
    PAGAMENTOS_INSCRICAO_FILTRADOS,
    {
      variables: {
        ligaId: ligaId ?? undefined,
        inicio: periodo.inicio,
        fim: periodo.fim,
        torneioId: filtros.torneioId ?? undefined,
        atletaId: filtros.atletaId ?? undefined,
        status: filtros.status ?? undefined,
        sortBy: 'DATA',
        direction: 'DESC',
        page,
        size: 10,
      },
      fetchPolicy: 'no-cache',
    },
  );

  const items = query.data?.pagamentosInscricaoFiltrados?.items ?? [];
  const totalItems = query.data?.pagamentosInscricaoFiltrados?.totalItems ?? 0;

  // Uma rejeicao da query (falha de rede) propaga para quem chamou -- quem monta a toolbar
  // decide como mostrar. O que NAO pode acontecer e o `TypeError: Cannot destructure property
  // 'data'` que uma resposta ausente/malformada produzia: o erro real ficava escondido atras de
  // um erro de tipo sem relacao com a causa. Aqui a resposta invalida vira erro explicito.
  const fetchTorneios = async (q: string): Promise<Array<{ id: string; label: string }>> => {
    const resultado = await apolloClient.query<TorneiosFiltradosData, TorneiosFiltradosVars>({
      query: TORNEIOS_FILTRADOS,
      variables: {
        nome: q || null,
        ligaId: ligaId ?? null,
        ativo: true,
        page: 0,
        size: 10,
        direction: 'ASC',
        sortBy: 'dataInicio',
      },
      fetchPolicy: 'no-cache',
    });
    const conexao = resultado?.data?.torneiosFiltrados;
    if (!conexao) {
      throw new Error('Resposta invalida da busca de torneios (torneiosFiltrados ausente).');
    }
    return (conexao.items || []).map((t) => ({ id: t.id, label: t.nome }));
  };

  const fetchLigas = async (q: string): Promise<Array<{ id: string; label: string }>> => {
    const conexao = await buscarLigasPorNome(q);
    if (!conexao) {
      throw new Error('Resposta invalida da busca de ligas (conexao ausente).');
    }
    return (conexao.items || []).map((liga) => ({ id: liga.id, label: liga.nome }));
  };

  return {
    items,
    totalItems,
    page,
    setPage,
    filtros,
    setFiltros,
    loading: query.loading,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
    fetchTorneios,
    fetchLigas,
  };
}
